// framework/client/helpers/MessagingClient/ConversationList.js
import { ComponentBase } from "../../../core/ComponentBase.js";
import { ConversationListItem } from "./ConversationListItem.js";
import { createAvatarElement } from "../avatarHelper.js"; 

export class ConversationList extends ComponentBase {
    static styles = `
        .conversation-list-wrapper {
            display: flex;
            flex-direction: column;
            height: 100%; /* Fill parent pane */
            overflow: hidden;
        }
        .conversation-list-header {
            padding: 0.75rem 1rem;
            border-bottom: 1px solid var(--bs-border-color);
            display: flex;
            justify-content: space-between; /* This will space out items */
            align-items: center;
            flex-shrink: 0;
            background-color: var(--bs-body-bg);
        }
        .user-profile-avatar-small {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background-color: var(--bs-secondary);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 1rem;
            margin-right: 0.5rem;
        }
        .user-profile-avatar-small img {
            width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
        }
        .header-title {
            font-size: 1.1rem;
            font-weight: 500;
            color: var(--bs-emphasis-color);
            flex-grow: 1;
        }
        .header-actions {
            display: flex;
            align-items: center;
        }

        .search-bar-wrapper {
            padding: 0.5rem 1rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            flex-shrink: 0;
        }
        .search-bar-wrapper input {
            border-radius: 20px;
            font-size: 0.9rem;
        }
        .conversations-scroll-area {
            flex-grow: 1;
            overflow-y: auto;
            scrollbar-width: thin;
            scrollbar-color: var(--bs-secondary-bg) var(--bs-tertiary-bg);
        }
        .conversations-scroll-area::-webkit-scrollbar { width: 6px; }
        .conversations-scroll-area::-webkit-scrollbar-track { background: var(--bs-tertiary-bg); }
        .conversations-scroll-area::-webkit-scrollbar-thumb { background-color: var(--bs-secondary-bg); border-radius: 3px; }

        .no-conversations {
            text-align: center;
            padding: 2rem;
            color: var(--bs-secondary-color);
        }
        
        .conv-last-message-snippet.typing-indicator {
            font-style: italic;
            color: var(--bs-primary); /* Example: Use primary theme color */
        }
    `;


    constructor(config) {
        super(config);
        this.conversations = []; // Array of full conversation data objects
        this.filteredConversations = [];
        this.activeConversationId = null;
        this.myContactId = null;
        this.currentProjectId = this.config.currentProjectId;

        this.listItemsMap = new Map(); // channelId -> ConversationListItem instance

        this.typingStatusMap = new Map(); // conversationId -> Map<contactId, { displayName, timeoutId }>
        this.TYPING_CLEAR_TIMEOUT_MS = 5000; // How long to show "typing" before it auto-clears

        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
        this.container = document.createElement('div');
        this.container.className = 'conversation-list-wrapper';

        this.renderHeader();
        this.renderSearchBar();

        this.scrollArea = document.createElement('div');
        this.scrollArea.className = 'conversations-scroll-area';
        this.container.appendChild(this.scrollArea);
    }

    setMyContactId(contactId) {
        this.myContactId = contactId;
        this.updateHeaderAvatar();
    }

async updateHeaderAvatar() {
        if (!this.container || !this.myContactId || !this.currentProjectId) return;

        const avatarContainer = this.container.querySelector('.user-profile-avatar-small'); // Target the container
        if (avatarContainer) {
            avatarContainer.innerHTML = ''; // Clear previous content

            let displayNameForAvatar = this.appClient.getSharedData('user')?.username || 'U';
            let avatarUrlForAvatar = null;

            try {
                const contact = await this.appClient.rpcClient.call('MessagingContactsRpc.findByContactId',
                    { contactId: this.myContactId },
                    { projectId: this.currentProjectId }
                );
                if (contact) {
                    displayNameForAvatar = contact.displayName || displayNameForAvatar;
                    avatarUrlForAvatar = contact.avatarUrl; // This is the relative path
                }
            } catch (err) {
                this.log.error("Failed to fetch own contact details for header avatar:", err);
            }
            
            const avatarElement = createAvatarElement({
                displayName: displayNameForAvatar,
                avatarUrl: avatarUrlForAvatar, // Pass the relative path
                sizeClass: 'small', // Matches existing .user-profile-avatar-small if styles are aligned
                // additionalClasses: ['user-profile-avatar-small'] // Keep if it has unique styles not covered by app-avatar-small
            });
            avatarContainer.appendChild(avatarElement);
        }
    }


   renderHeader() {
        const header = this.container.querySelector('.conversation-list-header') || document.createElement('div');
        header.className = 'conversation-list-header'; // Ensure class is set

        // --- MODIFIED AVATAR PART ---
        // Create a placeholder div for the avatar that updateHeaderAvatar will populate
        const avatarPlaceholder = document.createElement('div');
        avatarPlaceholder.className = 'user-profile-avatar-small'; // Keep this class for existing specific styles if any
        // --- END MODIFIED AVATAR PART ---

        const title = document.createElement('span');
        title.className = 'header-title';
        title.textContent = this.t('Chats');
        title.setAttribute('data-i18n', 'Chats');
        
        const actions = document.createElement('div');
        actions.className = 'header-actions';
        actions.innerHTML = `
            <button class="btn btn-outline-primary btn-sm" title="${this.t('New Chat')}" data-i18n-title="New Chat" data-action="new-chat">
                <i class="bi bi-plus"></i>
            </button>
        `;
        actions.querySelector('[data-action="new-chat"]').addEventListener('click', () => this.emit('newChatClicked'));

        // Assemble header
        header.innerHTML = ''; // Clear previous content before re-populating
        header.appendChild(avatarPlaceholder); // Add the placeholder
        header.appendChild(title);
        header.appendChild(actions);

        // Ensure header is correctly placed in the container
        if (!this.container.contains(header)) {
            if (this.container.firstChild) {
                this.container.insertBefore(header, this.container.firstChild);
            } else {
                this.container.appendChild(header);
            }
        }
        this.updateHeaderAvatar(); // Call to populate the avatar placeholder
        this.appClient.tx.applyTranslations(header);
    }


    renderSearchBar() {
        // ... (renderSearchBar remains the same, ensure it's appended to this.container once after header)
        const searchWrapper = document.createElement('div');
        searchWrapper.className = 'search-bar-wrapper';
        const searchInput = document.createElement('input');
        searchInput.type = 'search';
        searchInput.className = 'form-control';
        searchInput.placeholder = this.t('Search or start new chat');
        searchInput.setAttribute('data-i18n-placeholder', 'Search or start new chat');
        searchInput.addEventListener('input', (e) => this.filterConversations(e.target.value));
        searchWrapper.appendChild(searchInput);
        this.container.appendChild(searchWrapper);
    }

    async loadConversations() {
        if (!this.currentProjectId) {
            this.log.warn("Cannot load conversations: currentProjectId is not set.");
            this.conversations = [];
            this.filteredConversations = [];
            this.renderConversationItems();
            return;
        }
        try {
            const result = await this.appClient.rpcClient.call('MessagingRpc.listMyChannels',
                { page: 1, pageSize: 100 },
                { projectId: this.currentProjectId }
            );
            this.conversations = result.data || [];
            this.appClient.setSharedData('allUserConversations', this.conversations);
            this.filterConversations(this.container.querySelector('.search-bar-wrapper input')?.value || ''); // Apply current filter
        } catch (error) {
            this.log.error("Failed to load conversations:", error);
            if (this.scrollArea) { // Check if scrollArea is defined
                this.scrollArea.innerHTML = `<p class="no-conversations">${this.t('Failed to load chats.')}</p>`;
                this.appClient.tx.applyTranslations(this.scrollArea);
            }
        }
    }

    filterConversations(searchTerm = "") { // Default searchTerm to empty string
        const lowerSearchTerm = searchTerm.toLowerCase();
        if (!lowerSearchTerm) {
            this.filteredConversations = [...this.conversations];
        } else {
            this.filteredConversations = this.conversations.filter(conv =>
                conv.displayName?.toLowerCase().includes(lowerSearchTerm) ||
                (conv.lastMessageSnippet && typeof conv.lastMessageSnippet === 'string' && conv.lastMessageSnippet.toLowerCase().includes(lowerSearchTerm))
            );
        }
        this.renderConversationItems();
    }

    renderConversationItems() {
        if (!this.scrollArea) return;
        this.scrollArea.innerHTML = '';

        if (this.filteredConversations.length === 0) {
            this.scrollArea.innerHTML = `<p class="no-conversations">${this.t('No conversations yet.')}</p>`;
            this.appClient.tx.applyTranslations(this.scrollArea);
            return;
        }

        this.filteredConversations.sort((a, b) =>
            new Date(b.lastMessageTimestamp || 0) - new Date(a.lastMessageTimestamp || 0)
        );

        this.filteredConversations.forEach(convData => {
            let listItem = this.listItemsMap.get(convData.channelId);

            // --- PASS TYPING INFO ---
            const typingUsersInConv = this.typingStatusMap.get(convData.channelId);
            const typingDisplayNames = typingUsersInConv ? Array.from(typingUsersInConv.values()).map(u => u.displayName) : [];
            // --- END PASS ---

            if (!listItem) {
                listItem = new ConversationListItem({
                    appClient: this.appClient,
                    instanceId: `convItem-${convData.channelId}`,
                    eventGroup: `${this.eventGroup}-item-${convData.channelId}`,
                    conversationData: convData,
                    isActive: convData.channelId === this.activeConversationId,
                    typingDisplayNames: typingDisplayNames // Pass initial typing names
                });
                listItem.initialize(); // initialize should be called by createManagedComponent if used, or manually
                listItem.on('conversationSelected', (e) => this.handleConversationItemSelected(e));
                this.listItemsMap.set(convData.channelId, listItem);
            } else {
                // Update existing item with new data and typing names
                listItem.updateData(convData, convData.channelId === this.activeConversationId, typingDisplayNames);
            }
            this.scrollArea.appendChild(listItem.render());
        });
        this.appClient.tx.applyTranslations(this.scrollArea);
    }

    handleConversationItemSelected({ conversationId }) {
        const previouslyActiveId = this.activeConversationId;
        this.activeConversationId = conversationId;

        // Update newly active item
        const newActiveItem = this.listItemsMap.get(conversationId);
        if (newActiveItem) {
            const typingUsers = this.typingStatusMap.get(conversationId);
            const typingNames = typingUsers ? Array.from(typingUsers.values()).map(u => u.displayName) : [];
            newActiveItem.updateData(newActiveItem.conversationData, true, typingNames);
        }
        // Update previously active item (if different)
        if (previouslyActiveId && previouslyActiveId !== conversationId) {
            const oldActiveItem = this.listItemsMap.get(previouslyActiveId);
            if (oldActiveItem) {
                const typingUsersOld = this.typingStatusMap.get(previouslyActiveId);
                const typingNamesOld = typingUsersOld ? Array.from(typingUsersOld.values()).map(u => u.displayName) : [];
                oldActiveItem.updateData(oldActiveItem.conversationData, false, typingNamesOld);
            }
        }
        this.emit('conversationSelected', { conversationId });
    }


    updateTypingStatusForConversation({ conversationId, contactId, displayName, isTyping }) {
        if (!this.typingStatusMap.has(conversationId)) {
            this.typingStatusMap.set(conversationId, new Map());
        }
        const conversationTypers = this.typingStatusMap.get(conversationId);

        if (isTyping) {
            if (conversationTypers.has(contactId)) {
                clearTimeout(conversationTypers.get(contactId).timeoutId);
            }
            const timeoutId = setTimeout(() => {
                conversationTypers.delete(contactId);
                if (conversationTypers.size === 0) {
                    this.typingStatusMap.delete(conversationId);
                }
                this.updateSpecificListItem(conversationId);
            }, this.TYPING_CLEAR_TIMEOUT_MS);
            conversationTypers.set(contactId, { displayName, timeoutId });
        } else {
            if (conversationTypers.has(contactId)) {
                clearTimeout(conversationTypers.get(contactId).timeoutId);
                conversationTypers.delete(contactId);
            }
            if (conversationTypers.size === 0) {
                this.typingStatusMap.delete(conversationId);
            }
        }
        this.updateSpecificListItem(conversationId);
    }

    updateSpecificListItem(conversationId) {
        const listItemInstance = this.listItemsMap.get(conversationId);
        if (listItemInstance) {
            const typingUsers = this.typingStatusMap.get(conversationId);
            const typingDisplayNames = typingUsers ? Array.from(typingUsers.values()).map(u => u.displayName) : [];
            listItemInstance.updateData(
                listItemInstance.conversationData, // Pass current conversation data
                listItemInstance.conversationData.channelId === this.activeConversationId, // Pass current active state
                typingDisplayNames // Pass new typing names
            );
        }
    }



    handleIncomingMessage(newMessageEvent, isConfirmation = false) {
        const { conversationId, projectId: messageProjectId, clientTempId } = newMessageEvent;

        if (messageProjectId !== this.currentProjectId) {
            // ...
            return;
        }

        let conversation = this.conversations.find(c => c.channelId === conversationId);

        if (conversation) {
            // If it's a confirmation of an optimistic message, we might not need to increment unread count
            // if the optimistic update already handled the UI for the sender.
            // However, the last message snippet and timestamp should still be updated.
            conversation.lastMessageSnippet = newMessageEvent.content?.substring(0, 30) || (newMessageEvent.attachments?.length > 0 ? "Attachment" : "Message");
            conversation.lastMessageTimestamp = newMessageEvent.timestamp;

            if (!isConfirmation && conversationId !== this.activeConversationId && newMessageEvent.senderContactId !== this.myContactId) {
                conversation.unreadCount = (conversation.unreadCount || 0) + 1;
            }
            this.conversations.sort((a, b) => new Date(b.lastMessageTimestamp || 0) - new Date(a.lastMessageTimestamp || 0));
            this.filterConversations(this.container.querySelector('.search-bar-wrapper input')?.value || '');
        } else {
            // If it's a new conversation (even if it's a confirmation for an optimistic message
            // that started this new conversation), load all conversations to get its details.
            this.loadConversations().then(() => {
                const newConv = this.conversations.find(c => c.channelId === conversationId);
                if (newConv && conversationId !== this.activeConversationId && newMessageEvent.senderContactId !== this.myContactId) {
                    newConv.unreadCount = (newConv.unreadCount || 0) + 1;
                }
                this.filterConversations(this.container.querySelector('.search-bar-wrapper input')?.value || '');
            });
        }
    }

    async addNewConversation(newConvData) {
        const existingIndex = this.conversations.findIndex(c => c.channelId === newConvData.channelId);
        if (existingIndex === -1) {
            // Ensure essential fields for display are present from the event payload
            newConvData.displayName = newConvData.displayName || newConvData.name || "New Group"; // Fallback for group name
            newConvData.displayAvatar = newConvData.displayAvatar || newConvData.avatarUrl;
            newConvData.lastMessageSnippet = newConvData.lastMessageSnippet || this.t('No messages yet...');
            newConvData.lastMessageTimestamp = newConvData.lastMessageTimestamp || new Date().toISOString();
            newConvData.unreadCount = newConvData.unreadCount || 0;

            this.conversations.push(newConvData);
            this.filterConversations(this.container.querySelector('.search-bar-wrapper input')?.value || '');
            this.log.info(`Added new conversation to list: ${newConvData.channelId}`);
        } else {
            this.log.info(`NewChannel event for existing conversation ${newConvData.channelId}, treating as update.`);
            this.handleConversationUpdate(newConvData); // Let existing update logic handle it
        }
    }

    handleConversationUpdate(updatedConvData) {
        if (updatedConvData.projectId !== this.currentProjectId) {
            this.log.debug(`Ignoring conversation update for project ${updatedConvData.projectId} (current: ${this.currentProjectId})`);
            return;
        }
        const index = this.conversations.findIndex(c => c.channelId === updatedConvData.channelId);
        if (index !== -1) {
            const oldConversation = this.conversations[index];
            const unreadCountToPreserve = (this.activeConversationId === updatedConvData.channelId)
                ? 0
                : oldConversation.unreadCount;

            // Merge, ensuring new display fields from server take precedence
            this.conversations[index] = {
                ...oldConversation,
                ...updatedConvData,
                displayName: updatedConvData.displayName || updatedConvData.name || oldConversation.displayName,
                displayAvatar: updatedConvData.displayAvatar || updatedConvData.avatarUrl || oldConversation.displayAvatar,
                unreadCount: updatedConvData.unreadCount !== undefined ? updatedConvData.unreadCount : unreadCountToPreserve
            };

            if (this.activeConversationId === updatedConvData.channelId) {
                this.emit('activeConversationDataUpdated', this.conversations[index]);
            }
        } else {
            // If it's an update for a channel not in the list, it's effectively a new channel for this user
            this.log.info(`ConversationUpdate for a new channel ${updatedConvData.channelId}. Adding it.`);
            const newConvData = {
                unreadCount: 0,
                lastMessageSnippet: this.t('No messages yet...'),
                lastMessageTimestamp: new Date().toISOString(),
                ...updatedConvData,
                displayName: updatedConvData.displayName || updatedConvData.name || "New Group",
                displayAvatar: updatedConvData.displayAvatar || updatedConvData.avatarUrl
            };
            this.conversations.push(newConvData);
        }
        this.filterConversations(this.container.querySelector('.search-bar-wrapper input')?.value || '');
    }

    resetUnreadCount(conversationId) {
        const conversation = this.conversations.find(c => c.channelId === conversationId);
        if (conversation && conversation.unreadCount > 0) {
            conversation.unreadCount = 0;
            const listItemInstance = this.listItemsMap.get(conversationId);
            if (listItemInstance) {
                listItemInstance.updateData(conversation, true);
            }
        }
    }

    removeConversation(channelId) {
        const index = this.conversations.findIndex(c => c.channelId === channelId);
        if (index !== -1) {
            this.conversations.splice(index, 1);
        }
        const filteredIndex = this.filteredConversations.findIndex(c => c.channelId === channelId);
        if (filteredIndex !== -1) {
            this.filteredConversations.splice(filteredIndex, 1);
        }

        const listItemInstance = this.listItemsMap.get(channelId);
        if (listItemInstance) {
            listItemInstance.destroy();
            this.listItemsMap.delete(channelId);
        }
        this.renderConversationItems(); // Re-render the list
    }


    async render() {
        if (!this.container.querySelector('.conversation-list-header')) {
            this.renderHeader();
        }
        if (!this.container.querySelector('.search-bar-wrapper')) {
            this.renderSearchBar();
        }
        if (!this.scrollArea && this.container) {
            this.scrollArea = document.createElement('div');
            this.scrollArea.className = 'conversations-scroll-area';
            this.container.appendChild(this.scrollArea);
        }
        await this.renderConversationItems(); // Made async
        return this.container;
    }

    destroy() {
        this.typingStatusMap.forEach(conversationTypers => {
            conversationTypers.forEach(user => clearTimeout(user.timeoutId));
        });
        this.typingStatusMap.clear();

        this.listItemsMap.forEach(item => item.destroy());
        this.listItemsMap.clear();
        super.destroy();
    }
}